import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ClientProvider, useClient } from "./context/ClientContext";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { DashboardPage } from "./pages/DashboardPage";
import { MonthDetailPage } from "./pages/MonthDetailPage";
import { ImageLibraryPage } from "./pages/ImageLibraryPage";
import { AgencyQueuePage } from "./pages/AgencyQueuePage";
import { CreativeStudioPage } from "./pages/CreativeStudioPage";
import ReadyToPostPage from "./pages/ReadyToPostPage";
import ClientSettingsPage from "./pages/ClientSettingsPage";
import PricingPage from "./pages/PricingPage";
import LeadsDashboard from "./pages/LeadsDashboard";
import { Toaster } from "./components/ui/toaster";
import ClientPortalPage from "./pages/ClientPortalPage";
import HowItWorksPage from "./pages/HowItWorksPage";
import "./App.css";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#003C59]">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AppRoutes() {
  const { activeClient, loading } = useClient();
  const ck = activeClient?.id || 'loading';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#003C59]">
        <div className="text-white text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/portal/:clientSlug/:token" element={<ClientPortalPage />} />
      <Route path="/portal/:token" element={<ClientPortalPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Protected routes */}
      <Route path="/" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage key={ck} /></ProtectedRoute>} />
      <Route path="/month/:year/:month" element={<ProtectedRoute><MonthDetailPage key={ck} /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><ImageLibraryPage key={ck} /></ProtectedRoute>} />
      <Route path="/ideas" element={<ProtectedRoute><Navigate to="/agency" replace /></ProtectedRoute>} />
      <Route path="/agency" element={<ProtectedRoute><AgencyQueuePage key={ck} /></ProtectedRoute>} />
      <Route path="/studio" element={<ProtectedRoute><CreativeStudioPage key={ck} /></ProtectedRoute>} />
      <Route path="/publish" element={<ProtectedRoute><ReadyToPostPage key={ck} /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><ClientSettingsPage key={ck} /></ProtectedRoute>} />
      <Route path="/leads" element={<ProtectedRoute><LeadsDashboard /></ProtectedRoute>} />

      {/* Client View Routes (public share links) */}
      <Route path="/client/share/:slug/:year" element={<DashboardPage readOnly={true} key={ck} />} />
      <Route path="/client/:slug/:year" element={<DashboardPage readOnly={true} key={ck} />} />
      <Route path="/client" element={<DashboardPage readOnly key={ck} />} />
      <Route path="/client/month/:year/:month" element={<MonthDetailPage readOnly key={ck} />} />
      <Route path="/client/ideas" element={<Navigate to="/client" replace />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ClientProvider>
          <AppRoutes />
          <Toaster />
        </ClientProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
